import React, { useState, useRef, useEffect } from "react";
import "./Toolsmenu.css";
import MergeInvoicePopup, { MergeFormState } from "../MergeInvoicePopup/MergeInvoicePopup";
import BulkEditPopup, {
  BulkEditOptions,
  BulkEditPayload,
  BulkEditToast,
  BulkEditToastState,
} from "../BulkeditPopup/BulkEditPopup";
import {
  GetInvoicesByIdsService,
  MergeInvoicesService,
 
} from "../../../../Services/InvoiceService/InvoiceService";
import { BulkEditInvoicesService} from "../../../../Services/BulkEditService/Bulkeditinvoicesservice"
import AlertMessage from "../../../../Components/AlertMessage/AlertMessage";
// ─── Types ───────────────────────────────────────────────────────────────────

interface MergeWarning {
  differentTotals: boolean;
  differentDates: boolean;
  differentSuppliers: boolean;
  tooManyItems: boolean;
}

export interface BulkEditResult {
  updatedIds: number[];
  failed: { id: number; reason?: string }[];
}

const MAX_MERGE_ITEMS_WITHOUT_WARNING = 20;

const DEFAULT_MAX_BULK_EDIT_ITEMS = 200;
const BULK_EDIT_UNSUPPORTED_SCREENS = ["archive"];
const BULK_EDIT_CONFLICT_MESSAGE =
  "Another user changed or deleted one or more selected items. Please try again.";
const BULK_EDIT_NETWORK_MESSAGE =
  "The update could not be completed. Nothing was changed. Please retry.";


interface ToolsMenuProps {
  selectedIds: number[];
  onMerge?: (sourceIds: number[], mergedInvoiceId: number) => void;
  showMerge?: boolean;
  onFlag: (ids: number[]) => void;
  onUnflag: (ids: number[]) => void;
  onDelete: (ids: number[]) => void;
  invoices?: any[];
  userId?: string;
  disabled?: boolean;

  // ── Bulk Edit ──
  /** Current screen: "inbox" | "toReview" | "ready" | "exported" | "archive" */
  screen: string;
  /** Same option lists the single-document edit form uses */
  bulkEditOptions: BulkEditOptions;
  /** Parent refetches the list (keeping client, filters, sort, page) */
  onBulkEditComplete: (result: BulkEditResult) => void;
  /** Parent filters/navigates to the failed rows */
  onViewFailedItems?: (ids: number[]) => void;
  maxBulkEditItems?: number;
}

const isMergeConflict = (error: any): boolean => {
  return (
    error?.status === 409 ||
    error?.response?.status === 409 ||
    error?.code === "MERGE_IN_PROGRESS"
  );
};

const MERGE_CONFLICT_MESSAGE =
  "Another user is currently merging one or more selected items. Please try again.";

function detectWarnings(
  ids: number[],
  invoices: any[] = [],
): MergeWarning {
  const selected = invoices.filter((inv) => ids.includes(inv.id));
  const totals = new Set(selected.map((i) => i.totalAmount));
  const dates = new Set(selected.map((i) => i.invoiceDate));
  const suppliers = new Set(selected.map((i) => i.supplier));
  return {
    differentTotals: totals.size > 1,
    differentDates: dates.size > 1,
    differentSuppliers: suppliers.size > 1,
    tooManyItems: ids.length > MAX_MERGE_ITEMS_WITHOUT_WARNING,
  };
}

const normalizeInvoiceItem = (raw: any) => {
  if (!raw) return raw;

  const pick = (...keys: string[]) => {
    for (const k of keys) {
      if (raw[k] !== undefined && raw[k] !== null) return raw[k];
    }
    return undefined;
  };

  const trimStr = (v: any) => (typeof v === "string" ? v.trim() : v);

  return {
    ...raw,
    invoiceId: pick("invoiceId", "InvoiceId"),
    documentUrl: pick("documentUrl", "DocumentUrl"),
    type: trimStr(pick("type", "Type")),
    invoiceDate: pick("invoiceDate", "InvoiceDate"),
    supplier: pick("supplier", "Supplier"),
    documentReference: pick("documentReference", "DocumentReference"),
    dueDate: pick("dueDate", "DueDate"),
    category: trimStr(pick("category", "Category")),
    description: pick("description", "Description"),
    currency: trimStr(pick("currency", "Currency")),
    totalAmount: pick("totalAmount", "TotalAmount"),
    taxAmount: pick("taxAmount", "TaxAmount"),
  };
};

// ─── Merge Confirmation Modal ─────────────────────────────────────────────────

interface MergeModalProps {
  warnings: MergeWarning;
  onCancel: () => void;
  onContinue: () => void;
  loading: boolean;
}

const MergeModal: React.FC<MergeModalProps> = ({ warnings, onCancel, onContinue, loading }) => {
  const warningLines: string[] = [];
  if (warnings.tooManyItems) {
    // When merging more than 20 items, show only this warning.
    warningLines.push("You are merging more than 20 items. Are you sure you want to continue?");
  } else {
    if (warnings.differentTotals)
      warningLines.push("The selected items have different total amounts.");
    if (warnings.differentDates)
      warningLines.push("The selected items have different dates.");
    if (warnings.differentSuppliers)
      warningLines.push("The selected items have different suppliers.");
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div className="tm-overlay" role="dialog" aria-modal="true" aria-labelledby="tm-modal-title">
      <div className="tm-backdrop" onClick={onCancel} />

      <div className="tm-modal">
        <div className="tm-modal-header">
          <span className="tm-modal-icon">⊞</span>
          <h2 className="tm-modal-title" id="tm-modal-title">Merge Items</h2>
          <button className="tm-modal-close" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        <div className="tm-modal-body">
          <p className="tm-modal-question">Are you sure you want to merge these items?</p>
          {warningLines.length > 0 && (
            <ul className="tm-modal-warnings">
              {warningLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="tm-modal-footer">
          <button className="tm-btn tm-btn-cancel" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button className="tm-btn tm-btn-continue" onClick={onContinue} disabled={loading}>
            {loading ? "Loading..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Merge Error Alert ─────────────────────────────────────────────────────

interface MergeErrorAlertProps {
  message: string;
  onClose: () => void;
}

const MergeErrorAlert: React.FC<MergeErrorAlertProps> = ({ message, onClose }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="tm-overlay tm-overlay--alert" role="alertdialog" aria-modal="true" aria-labelledby="tm-alert-title">
      <div className="tm-backdrop" onClick={onClose} />

      <div className="tm-modal tm-modal--alert">
        <div className="tm-modal-header">
          <span className="tm-modal-icon">⚠</span>
          <h2 className="tm-modal-title" id="tm-alert-title">Merge Unavailable</h2>
          <button className="tm-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="tm-modal-body">
          <p className="tm-modal-question">{message}</p>
        </div>

        <div className="tm-modal-footer">
          <button className="tm-btn tm-btn-continue" onClick={onClose} autoFocus>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Tools Menu ───────────────────────────────────────────────────────────────

const ToolsMenu: React.FC<ToolsMenuProps> = ({
  selectedIds,
  onMerge,
  showMerge = true,
  onFlag,
  onUnflag,
  onDelete,
  invoices = [],
  userId,
  disabled = false,
  screen,
  bulkEditOptions,
  onBulkEditComplete,
  onViewFailedItems,
  maxBulkEditItems = DEFAULT_MAX_BULK_EDIT_ITEMS,
}) => {
  const [open, setOpen] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeWarnings, setMergeWarnings] = useState<MergeWarning>({
    differentTotals: false,
    differentDates: false,
    differentSuppliers: false,
    tooManyItems: false,
  });

  // --- Merge popup state ---
  const [showMergePopup, setShowMergePopup] = useState(false);
  const [mergeItems, setMergeItems] = useState<any[]>([]);
  const [loadingMergeItems, setLoadingMergeItems] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  // --- Bulk edit state ---
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkToast, setBulkToast] = useState<BulkEditToastState | null>(null);
  const [bulkRowVersions, setBulkRowVersions] = useState<Record<number, string>>({});

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const noSelection = selectedIds.length === 0;
  const bulkEditSupported = !BULK_EDIT_UNSUPPORTED_SCREENS.includes((screen ?? "").toLowerCase());
  const overBulkLimit = selectedIds.length > maxBulkEditItems;

  // ── Action handlers ──

  const handleMergeClick = () => {
    setOpen(false);
    const warnings = detectWarnings(selectedIds, invoices);
    setMergeWarnings(warnings);
    setShowMergeModal(true);
  };

  // Fetch full invoice details for the selected ids, then open the merge popup
  const handleMergeConfirm = async () => {
    if (!userId) {
      setMergeError("No client selected.");
      return;
    }

    setLoadingMergeItems(true);
    setMergeError(null);
    try {
      const rawItems = await GetInvoicesByIdsService({ invoiceIds: selectedIds });
      const normalized = rawItems.map(normalizeInvoiceItem);

      // Preserve the order the user selected them in.
      const orderedItems = selectedIds
        .map((id) => normalized.find((item: any) => item.invoiceId === id))
        .filter(Boolean);

      if (orderedItems.length === 0) {
        console.error(
          "MergeInvoicePopup: no matching invoices found. Raw response was:",
          rawItems,
        );
        setMergeError("Couldn't load invoice details for the selected items.");
        return;
      }

      if (orderedItems.length < selectedIds.length) {
        console.warn(
          `MergeInvoicePopup: only matched ${orderedItems.length} of ${selectedIds.length} selected invoices.`,
          { selectedIds, normalized },
        );
      }

      setMergeItems(orderedItems);
      setShowMergeModal(false);
      setShowMergePopup(true);
    } catch (error: any) {
      console.error("Failed to load invoice details for merge:", error);
      if (isMergeConflict(error)) {
        setMergeError(error?.message ?? MERGE_CONFLICT_MESSAGE);
        setShowMergeModal(false);
      } else {
        setMergeError("Failed to load invoice details for merge.");
      }
    } finally {
      setLoadingMergeItems(false);
    }
  };

  const handleMergePopupCancel = () => {
    setShowMergePopup(false);
    setMergeItems([]);
  };

  const handleMergePopupMerge = async (formData: MergeFormState) => {
    setMergeLoading(true);
    setMergeError(null);

    try {
      const result = await MergeInvoicesService({
        // Invoice IDs in the user's selection order (first = data template)
        invoiceIds: selectedIds,

        // Form field overrides — only send if user actually filled them in
        // Backend uses these instead of copying from the first invoice
        type:              formData.type              || undefined,
        invoiceDate:       formData.invoiceDate       || undefined,
        supplier:          formData.supplier          || undefined,
        documentReference: formData.documentReference || undefined,
        dueDate:           formData.dueDate           || undefined,
        category:          formData.category          || undefined,
        description:       formData.description       || undefined,
        currency:          formData.currency          || undefined,
        totalAmount:       formData.totalAmount ? parseFloat(formData.totalAmount) : undefined,
        taxAmount:         formData.taxAmount   ? parseFloat(formData.taxAmount)   : undefined,
      });

      // result = { mergedInvoiceId: 4, sourceInvoiceIds: [1, 2, 3] }
      setShowMergePopup(false);
      setMergeItems([]);

      // Tell Inbox to remove source invoices and add the new merged invoice
      onMerge?.(selectedIds, result.mergedInvoiceId);
    } catch (error: any) {
      console.error("Merge failed:", error);
      if (isMergeConflict(error)) {
        setMergeError(error?.message ?? MERGE_CONFLICT_MESSAGE);
        setShowMergePopup(false);
        setMergeItems([]);
      } else {
        setMergeError(error?.message ?? "Merge failed. Please try again.");
      }
    } finally {
      setMergeLoading(false);
    }
  };

  // ── Bulk edit handlers ──

 const handleBulkEditClick = async () => {
  setOpen(false);
  setBulkError(null);
  setBulkRowVersions({});

  try {
    // Fresh read: the versions are what the user is about to edit
    const rawItems = await GetInvoicesByIdsService({ invoiceIds: selectedIds });
    const versions: Record<number, string> = {};
    rawItems.forEach((raw: any) => {
      const id = raw?.invoiceId ?? raw?.InvoiceId;
      const version = raw?.rowVersion ?? raw?.RowVersion;
      if (id != null && version) versions[id] = version;
    });
    setBulkRowVersions(versions);
  } catch (error) {
    console.error("Failed to load versions for bulk edit:", error);
    setBulkError("Couldn't load the latest data for the selected items. Please close and try again.");
  }

  setShowBulkEdit(true);
};

  const handleBulkEditCancel = () => {
    // Discards everything entered; the grid selection is untouched.
    setShowBulkEdit(false);
    setBulkError(null);
  };

  const handleBulkEditSubmit = async (fields: BulkEditPayload) => {
    if (!userId) {
      setBulkError("No client selected.");
      return;
    }
    if (Object.keys(bulkRowVersions).length === 0) {
      setBulkError("Couldn't verify the latest data for the selected items. Please close and try again.");
      return;
    }

    setBulkLoading(true);
    setBulkError(null);

    try {
      const result: BulkEditResult = await BulkEditInvoicesService({
        invoiceIds: selectedIds,
        businessClientId: userId,
        screen,
        rowVersions: bulkRowVersions,
        ...fields, // only touched fields are present
      });

      const updated = result.updatedIds.length;
      const failedIds = result.failed.map((f) => f.id);

      setShowBulkEdit(false);
      onBulkEditComplete(result); // parent refreshes list, keeping client/filters/sort/paging

      if (failedIds.length === 0) {
        // Same message component the Merge flow uses
        AlertMessage({
          type: "success",
          text: `${updated} ${updated === 1 ? "item" : "items"} updated successfully`,
        });
      } else {
        setBulkToast({
          variant: "partial",
          message: `${updated} items updated. ${failedIds.length} items could not be updated.`,
          failedIds,
        });
      }
    } catch (error: any) {
      console.error("Bulk edit failed:", error);
      // Modal stays open with values preserved; primary button becomes "Retry".
      setBulkError(
        isMergeConflict(error)
          ? BULK_EDIT_CONFLICT_MESSAGE
          : error?.message ?? BULK_EDIT_NETWORK_MESSAGE,
      );
    } finally {
      setBulkLoading(false);
    }
  };

  const handleFlag = () => {
    setOpen(false);
    onFlag(selectedIds);
  };

  const handleUnflag = () => {
    setOpen(false);
    onUnflag(selectedIds);
  };

  const handleDelete = () => {
    setOpen(false);
    onDelete(selectedIds);
  };

  // ── Render ──

  return (
    <>
      <div className="tm-wrapper" ref={menuRef}>
        <button
          className={`tm-trigger${open ? " tm-trigger--open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-haspopup="true"
          aria-expanded={open}
        >
          Tools
          <svg
            className="tm-caret"
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M1.5 3.5L5 7L8.5 3.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {open && (
          <div className="tm-dropdown" role="menu">
            {bulkEditSupported && (
              <button
                className="tm-option"
                role="menuitem"
                onClick={handleBulkEditClick}
                disabled={noSelection || overBulkLimit}
                title={
                  overBulkLimit
                    ? `You can bulk edit up to ${maxBulkEditItems} items at a time`
                    : noSelection
                      ? "Select at least 1 item to bulk edit"
                      : undefined
                }
              >
                Bulk Edit
              </button>
            )}
            {showMerge && (
              <button
                className="tm-option"
                role="menuitem"
                onClick={handleMergeClick}
                disabled={noSelection || selectedIds.length < 2}
                title={selectedIds.length < 2 ? "Select at least 2 items to merge" : undefined}
              >
                Merge
              </button>
            )}
            <button
              className="tm-option"
              role="menuitem"
              onClick={handleFlag}
              disabled={noSelection}
            >
              Flag
            </button>
            <button
              className="tm-option"
              role="menuitem"
              onClick={handleUnflag}
              disabled={noSelection}
            >
              Unflag
            </button>
            <div className="tm-divider" />
            <button
              className="tm-option tm-option--danger"
              role="menuitem"
              onClick={handleDelete}
              disabled={noSelection}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {showMergeModal && (
        <MergeModal
          warnings={mergeWarnings}
          onCancel={() => setShowMergeModal(false)}
          onContinue={handleMergeConfirm}
          loading={loadingMergeItems}
        />
      )}
      {mergeError && (
        <MergeErrorAlert message={mergeError} onClose={() => setMergeError(null)} />
      )}

      {showMergePopup && (
        <MergeInvoicePopup
          items={mergeItems}
          loading={mergeLoading}
          onCancel={handleMergePopupCancel}
          onMerge={handleMergePopupMerge}
        />
      )}

      {bulkEditSupported && showBulkEdit && (
        <BulkEditPopup
          count={selectedIds.length}
          // options={bulkEditOptions}
          loading={bulkLoading}
          error={bulkError}
          onCancel={handleBulkEditCancel}
          onSubmit={handleBulkEditSubmit}
        />
      )}

      {bulkToast && (
        <BulkEditToast
          {...bulkToast}
          onClose={() => setBulkToast(null)}
          onViewFailed={onViewFailedItems}
        />
      )}
    </>
  );
};

export default ToolsMenu;